import { useState } from "react";
import axios from "axios";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { Panel, Field, StatusBadge, formatDate } from "@/components/admin/ui";
import { centsToDisplay } from "@/lib/money";
import { useSaveStatus } from "./useSaveStatus";
import { inputClass, labelClass, saveBtnClass } from "./orgFormStyles";

const PAYMENT_STATUSES = ["Not Configured", "Configured"];

export default function OrgBillingTab({ org, quote, API, onSaved }) {
  const billing = org.billing || {};
  const [form, setForm] = useState(() => ({
    billing_contact_name: billing.billing_contact_name || "",
    billing_contact_email: billing.billing_contact_email || "",
    billing_contact_phone: billing.billing_contact_phone || "",
    subscription_start_date: billing.subscription_start_date || "",
    next_billing_date: billing.next_billing_date || "",
    payment_status: billing.payment_status || "Not Configured",
  }));
  const { saving, setSaving, error, setError, savedFlash, flash } = useSaveStatus();
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const { data } = await axios.patch(`${API}/admin/organizations/${org.org_id}/billing`, form, { withCredentials: true });
      onSaved(data);
      flash();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="org-billing-tab">
      <Panel title="Carried From Accepted Quote" subtitle="Platform-managed. No live payment provider is connected in this preview.">
        <div className="mb-4 flex items-center gap-3">
          <Field label="Quote ID" value={billing.quote_id} testId="org-billing-quote-id" />
          {quote && <StatusBadge status={quote.status} testId="org-billing-quote-status" />}
        </div>
        <div className="grid gap-5 sm:grid-cols-3">
          <Field label="Quote Accepted" value={formatDate(billing.accepted_at)} testId="org-billing-accepted-at" />
          <Field label="Billing Frequency" value={billing.billing_frequency} testId="org-billing-frequency" />
          <Field label="Payment Terms" value={billing.payment_terms} testId="org-billing-payment-terms" />
          <Field label="Initial Amount" value={centsToDisplay(billing.initial_amount_cents)} testId="org-billing-initial-amount" />
          <Field label="Recurring Amount" value={centsToDisplay(billing.recurring_amount_cents)} testId="org-billing-recurring-amount" />
        </div>
      </Panel>

      <Panel title="Billing Contact & Subscription">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className={labelClass}>Billing Contact Name</label>
            <input value={form.billing_contact_name} onChange={(e) => set({ billing_contact_name: e.target.value })} data-testid="org-billing-contact-name" className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Billing Contact Email</label>
            <input type="email" value={form.billing_contact_email} onChange={(e) => set({ billing_contact_email: e.target.value })} data-testid="org-billing-contact-email" className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Billing Contact Phone</label>
            <input value={form.billing_contact_phone} onChange={(e) => set({ billing_contact_phone: e.target.value })} data-testid="org-billing-contact-phone" className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Payment Status</label>
            <select value={form.payment_status} onChange={(e) => set({ payment_status: e.target.value })} data-testid="org-billing-payment-status" className={inputClass}>
              {PAYMENT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>Subscription Start Date</label>
            <input type="date" value={form.subscription_start_date || ""} onChange={(e) => set({ subscription_start_date: e.target.value })} data-testid="org-billing-subscription-start" className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Next Billing Date</label>
            <input type="date" value={form.next_billing_date || ""} onChange={(e) => set({ next_billing_date: e.target.value })} data-testid="org-billing-next-date" className={inputClass} />
          </div>
        </div>
      </Panel>

      {error && <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400">{error}</p>}
      <div className="flex items-center gap-4">
        <button type="button" onClick={save} disabled={saving} data-testid="org-billing-save-btn" className={saveBtnClass}>{saving ? "Saving…" : "Save Billing"}</button>
        {savedFlash && <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">Saved</span>}
      </div>
    </div>
  );
}
